import { useRef, useEffect, useState } from "react";
import { LoadingIcon } from "../../assets/icons";
import { useGetBapLogByPayRef } from "../../hooks/Query/useGetBapLogByPayRef";
import { createPointApprovalRequest } from "../../service/query/auth";

function ReasonModal({ actionType, onCancel, onConfirm, submitting }) {
    const [reason, setReason] = useState("");
    const isDebit = actionType === "debit";

    return (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center px-4">
            <div className="bg-white rounded-xl w-full max-w-[420px] p-6">
                <h4 className="text-[16px] font-semibold text-[#212121] mb-1 capitalize">
                    Request {actionType === "reverse" ? "reversal" : "debit"}
                </h4>
                <p className="text-[13px] text-[#667085] mb-4">
                    This will be sent to a checker for approval before any points move.
                    Please provide a reason.
                </p>

                <label className="block text-[12px] font-medium text-black mb-2">
                    Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Mistake debiting"
                    autoFocus
                    className="w-full px-3 py-2 border border-[#CED4DF] rounded-[5px] text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-900 mb-4"
                />

                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        disabled={submitting}
                        className="flex-1 border border-[#222222] text-[#000000] py-2 rounded text-sm hover:bg-gray-100 h-[40px]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onConfirm(reason)}
                        disabled={submitting || !reason.trim()}
                        className={`flex-1 text-white py-2 rounded text-sm font-medium h-[40px] flex items-center justify-center disabled:opacity-50 ${isDebit ? "bg-[#FA2323] hover:bg-red-600" : "bg-[#00305E] hover:bg-blue-800"
                            }`}
                    >
                        {submitting ? <LoadingIcon /> : "Submit for approval"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function PointsManagement() {
    const [paymentReference, setPaymentReference] = useState("");
    const [searchId, setSearchId] = useState("");
    const [searchEnabled, setSearchEnabled] = useState(false);
    const [openActionId, setOpenActionId] = useState(null);
    const [toast, setToast] = useState(null);
    const [pendingAction, setPendingAction] = useState(null); // { type: 'reverse' | 'debit', item }
    const [submitting, setSubmitting] = useState(false);

    const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const { data: bapData, isFetching: loadingBap, refetch: refetchBap } = useGetBapLogByPayRef({
        payment_ref: searchId,
        enabled: searchEnabled,
    });

    const bapLog = bapData?.data?.data ? [bapData.data.data] : [];

    const handleSearch = () => {
        if (!paymentReference) return;
        setSearchId(paymentReference);
        setSearchEnabled(true);
    };

    const actionBtnRefs = useRef({});

    useEffect(() => {
        const handleClickOutside = () => setOpenActionId(null);
        const handleScroll = () => setOpenActionId(null);

        if (openActionId !== null) {
            document.addEventListener("click", handleClickOutside);
            window.addEventListener("scroll", handleScroll, true);
        }
        return () => {
            document.removeEventListener("click", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [openActionId]);

    const openReasonModal = (type, item) => {
        setOpenActionId(null);
        setPendingAction({ type, item });
    };

    const handleConfirmAction = async (reason) => {
        if (!pendingAction) return;
        const { type, item } = pendingAction;

        setSubmitting(true);
        try {
            const res = await createPointApprovalRequest({
                action: type === "reverse" ? "reversal" : "debiting",
                payment_ref: item.payment_ref,
                reason,
            });
            setPendingAction(null);
            showToast(res?.data?.message || "Request raised and awaiting a checker.");
        } catch (err) {
            showToast(
                err?.response?.data?.message || `Failed to raise ${type} request`,
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-6 bg-white min-h-[90vh]">
            <div className="mb-[29px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment reference <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    value={paymentReference}
                    onChange={(e) => {
                        setPaymentReference(e.target.value);
                        setSearchEnabled(false);
                    }}
                    placeholder="Select"
                    className="w-full md:w-[700px] px-3 py-2 border border-gray-300 rounded-[5px] h-[35px] focus:outline-none focus:ring-1 focus:ring-blue-900 text-sm"
                />
            </div>

            <button
                onClick={handleSearch}
                disabled={!paymentReference || loadingBap}
                className="bg-[#00305E] hover:bg-blue-800 text-white px-8 py-2 rounded flex items-center justify-center w-[180px] h-[38px] text-sm mb-[70px]"
            >
                {loadingBap ? <LoadingIcon /> : "Search"}
            </button>

            {!searchEnabled && (
                <div className="bg-[#F5F5F5] h-[300px] flex items-center justify-center rounded-lg">
                    <p className="text-[#6D6D6D] text-[14px]">
                        Search for a payment reference ID to see results
                    </p>
                </div>
            )}

            {searchEnabled && (
                <div className="overflow-visible relative" style={{ minHeight: "300px" }}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left min-w-[900px]">
                            <thead className="bg-[#F8FAFC] h-[44px]">
                                <tr>
                                    <th className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">Membership ID ↓</th>
                                    <th className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">Payment ref ↓</th>
                                    <th className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">Points debited ↓</th>
                                    <th className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">Redemption type ↓</th>
                                    <th className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">Response ↓</th>
                                    <th className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">Action ↓</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingBap ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-10">
                                            <div className="flex justify-center"><LoadingIcon /></div>
                                        </td>
                                    </tr>
                                ) : bapLog.length > 0 ? (
                                    bapLog.map((item, index) => (
                                        <tr key={index} className="border-b transition-colors">
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{item.membership_id ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{item.payment_ref ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{item.points_debited ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{item.redeem_type ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{item.bap_status ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[14px] whitespace-nowrap relative">
                                                <button
                                                    ref={(el) => actionBtnRefs.current[index] = el}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenActionId(openActionId === index ? null : index);
                                                    }}
                                                    className="text-gray-500 hover:text-gray-800 font-bold text-lg"
                                                >
                                                    •••
                                                </button>
                                                {openActionId === index && (() => {
                                                    const rect = actionBtnRefs.current[index]?.getBoundingClientRect();
                                                    return (
                                                        <div
                                                            className="fixed bg-white shadow-xl rounded-md z-[9999] w-[110px] border border-gray-100 flex flex-col"
                                                            style={{ top: rect?.bottom + 4, left: rect?.left }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <button
                                                                onClick={() => openReasonModal("reverse", item)}
                                                                disabled={item.bap_status === "Failed"}
                                                                className={`w-full text-left px-4 py-3 text-[13px] border-b border-gray-100 ${item.bap_status === "Failed" ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-blue-50"}`}
                                                            >
                                                                Reverse
                                                            </button>
                                                            <button
                                                                onClick={() => openReasonModal("debit", item)}
                                                                disabled={item.bap_status === "Successful"}
                                                                className={`w-full text-left px-4 py-3 text-[13px] ${item.bap_status === "Successful" ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-blue-50"}`}
                                                            >
                                                                Debit
                                                            </button>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="text-center text-gray-400 py-10">
                                            No results found for "{searchId}"
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {pendingAction && (
                <ReasonModal
                    actionType={pendingAction.type}
                    submitting={submitting}
                    onCancel={() => setPendingAction(null)}
                    onConfirm={handleConfirmAction}
                />
            )}

            {toast && (
                <div className={`fixed bottom-6 right-6 z-[9999] px-5 py-3 rounded-lg shadow-lg text-white text-[14px] font-medium transition-all ${toast.type === "error" ? "bg-red-500" : "bg-[#00305E]"}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
}